import * as angular from "angular";
import EditJobRunName, {
  AddEditJobRun,
  openEditJobRun,
} from "./editJobRun.controller";
import { NavigationModel, Plant } from "../../models/navigation";
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import JobRunServiceName, {
  JobRunService,
} from "../../services/jobs/jobRun.service";
import LoaderServiceName, {
  LoaderService,
} from "../../components/loader/loader.service";
import NavigationServiceName, {
  NavigationService,
} from "../../services/navigation/navigation.service";
import AuthDataServiceName, {
  AuthDataService,
} from "../../services/auth/authData.service";
import { States, Strings } from "../../shared/config/constant.module";
import DateUtilityService from "../../services/utility/dateutility.service";
import template from "./jobRun.tpl.html";
import * as _ from "lodash";

let requires = [
  EditJobRunName,
  JobRunServiceName,
  LoaderServiceName,
  NavigationServiceName,
  AuthDataServiceName,
];

interface JobRunsViewController {
  navigation: NavigationModel;
  plants: Plant[];
  jobs: apsViewModels.JobItem[];
  jobRuns: apsViewModels.JobRunDisplayItem[];
  selectedPlant: Plant;
  selectedShop: apsViewModels.ShopNavigation;
  isApsioAdmin: boolean;
  shopChanged(): void;
  plantChanged(): void;
  editJobRun(jobRun: apsViewModels.JobRunItem): void;
  canAddJobRun(): boolean;
  addJobRun(): void;
  formatTime(date: Date): string;
  canRestore(jobRun: apsViewModels.JobRunItem): boolean;
  restoreJobRun(jobRun: apsViewModels.JobRunItem): void;
}

export default angular
  .module("aps.controller.jobRun", requires)
  .config(jobRunsConfig)
  .controller("JobRunsCtrl", jobRunsCtrl).name;

function jobRunsConfig($stateProvider: angular.ui.IStateProvider) {
  $stateProvider.state({
    name: States.JOB_RUNS,
    url: "/admin/jobRuns",
    controller: "JobRunsCtrl as vm",
    template: template,
    data: {
      pageTitle: "Job Runs",
      authorizePlantAdmin: true,
      v2Uri: "aps/settings/job-runs",
    },
  });
}

function jobRunsCtrl(
  $modal: mm.foundation.modal,
  $q: angular.IQService,
  jobRunService: JobRunService,
  loaderService: LoaderService,
  navigationService: NavigationService,
  authDataService: AuthDataService
) {
  let vm = this as JobRunsViewController;

  vm.isApsioAdmin = false;

  let timeZoneId = Strings.DEFAULT_TIMEZONE;
  vm.formatTime = function (date: Date) {
    return DateUtilityService.formatTime(date, timeZoneId);
  };

  function getJobsRuns(): angular.IPromise<apsViewModels.JobRunItem[]> {
    return jobRunService.getJobRuns(vm.selectedShop.id).then((jobRuns) => {
      _.forEach(jobRuns, (jobRun: AddEditJobRun) => {
        jobRun.job = _.find(vm.jobs, { id: jobRun.jobId });
      });
      vm.jobRuns = jobRuns;
      return vm.jobRuns;
    });
  }

  vm.plants = [];
  let navPromise = navigationService
    .getNavigation()
    .then(function (navigation) {
      vm.navigation = navigation;

      let plants = navigation.plants();
      vm.plants = _.filter(plants, (plant: any) => {
        return authDataService.isPlantAdmin(plant.id);
      });

      if (
        vm.navigation.selectedPlant &&
        _.includes(vm.plants, vm.navigation.selectedPlant)
      ) {
        vm.selectedPlant = vm.navigation.selectedPlant;
      } else {
        vm.selectedPlant = _.first(vm.plants);
      }

      vm.isApsioAdmin = authDataService.isAPSIOAdmin();
    });

  let jobPromise = jobRunService.getJobs().then((jobs) => (vm.jobs = jobs));
  loaderService.loadingPromise(
    $q.all([navPromise, jobPromise]).then(() => {
      vm.plantChanged();
    })
  );

  vm.plantChanged = () => {
    timeZoneId = vm.selectedPlant.timeZoneId;

    if (
      vm.navigation.selectedShop &&
      _.includes(vm.selectedPlant.shops, vm.navigation.selectedShop)
    ) {
      vm.selectedShop = vm.navigation.selectedShop;
    } else {
      vm.selectedShop = _.first(vm.selectedPlant.shops);
    }

    vm.shopChanged();
  };

  vm.shopChanged = () => {
    vm.jobRuns = undefined;
    loaderService.loadingPromise(getJobsRuns());
  };

  let loadModalJobRunEditor = function (jobRun?: apsViewModels.JobRunItem) {
    let result = openEditJobRun(
      {
        jobRun: jobRun,
        shop: vm.selectedShop,
        plant: vm.selectedPlant,
        jobRuns: vm.jobRuns,
        jobs: vm.jobs,
      },
      $modal
    );

    result.then(function (editedJobRun) {
      if (editedJobRun) {
        let job = _.find(vm.jobs, { id: editedJobRun.jobId });
        if (jobRun) {
          _.assign(
            jobRun,
            {
              job: job,
            },
            editedJobRun
          );
        } else {
          vm.jobRuns.unshift(
            _.assign(
              {
                legacyId: null,
                job: job,
              },
              editedJobRun
            )
          );
        }
      }
    });
  };

  vm.editJobRun = (jobRun: apsViewModels.JobRunItem) => {
    if ((jobRun || "") !== "") {
      loadModalJobRunEditor(jobRun);
    }
  };

  vm.canAddJobRun = () => {
    if (!!vm.jobRuns && vm.jobRuns.length > 0) {
      let jobrunStart = vm.formatTime(vm.jobRuns[0].startDate).substring(0, 10);
      let today = vm.formatTime(new Date()).substring(0, 10);
      return !(
        vm.jobRuns.length > 0 &&
        jobrunStart === today &&
        !this.isApsioAdmin
      );
    }
    return true;
  };

  vm.addJobRun = () => {
    loadModalJobRunEditor(undefined);
  };

  vm.canRestore = (jobRun: apsViewModels.JobRunItem) => {
    if (!this.isApsioAdmin) {
      return false;
    }

    return vm.jobRuns && vm.jobRuns.length > 1 && vm.jobRuns[1] === jobRun;
  };

  vm.restoreJobRun = (jobRun: apsViewModels.JobRunItem) => {
    loaderService.loadingPromise(
      jobRunService
        .restoreJobRun({
          id: jobRun.id,
          shopId: jobRun.shopId,
        })
        .then(() => {
          jobRun.endDate = null;
          vm.jobRuns.splice(0, 1);
        })
    );
  };

  return vm;
}



// WEBPACK FOOTER //
// ./src/app/controllers/jobRun/jobRun.controller.ts